import { useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';

import { CustomerTripOrderStatus, generateApolloClientHeaders, GRAPHQL_PAYLOAD_CONST } from '../../../modules';
import { phoenixAccountGetUpcomingTripsBasicInfo } from '../../../modules/graph';
import {
  phoenixAccountDttGetLoyaltyCurrencies,
  phoenixAccountDttGetMyActivityRewardsEarned,
  phoenixAccountDttGetUpcomingCustomerTrips,
} from '../../../modules/graphV2';
import { updateImageDomain } from '../../../modules/utils/imageUtility';
import { getHotelImageUrl } from '../../EarningsSummary/earningsSummaryHelper';

import {
  NUA_UXL_RESERVATION_STATUS_FOR_GUARANTEED,
  NUA_UXL_RESERVATION_STATUS_FOR_REQUESTED,
  NUA_UXL_STATUS_INFO_GUARANTEED,
  NUA_UXL_STATUS_INFO_PENDING,
  NUA_UXL_STATUS_TYPE,
  NUA_UXL_TRIP_STATUS_APPROVED,
  NUA_UXL_TRIP_STATUS_PENDING,
  WALLET_AWARD_STATUS,
  WALLET_NUA_AVAILABLE_ITEM_ID,
  normalizeNuaUxlStatusType,
} from '../WalletAwardsSection.constants';
import { WalletNuaItem, WalletFnaItem, WalletNuaExpiryGroup, WalletNightlyUpgradeAwardsSectionStatus } from '../types';

export const getNuaAvailableCount = (items: WalletNuaItem[]): number =>
  items.filter(item => item.status === WALLET_AWARD_STATUS.AVAILABLE).reduce((sum, item) => sum + (item.count || 0), 0);

export const getNuaTotalCount = (items: WalletNuaItem[]): number =>
  items.reduce((sum, item) => sum + (item.count || 0), 0);

type CertEdge = {
  node?: {
    awardType?: { code?: string | null; description?: string | null; label?: string | null } | null;
    expirationDate?: string | null;
    numberOfNights?: number | null;
    isCancellable?: boolean | null;
  } | null;
};

type FnaReservationDetail = {
  propertyId?: string | null;
  arrivalDate?: string | null;
  certificateNumber?: number | null;
  certificateSource?: string | null;
  confirmationNumber?: string | null;
  statusType?: string | null;
  expirationDate?: string | null;
};

type FnaAvailableInfo = {
  certificateNumber?: number | null;
  expirationDate?: string | null;
};

export function buildFnaExpirationByCertificateNumber(availableInfo?: FnaAvailableInfo[] | null): Map<number, string> {
  const map = new Map<number, string>();

  (availableInfo ?? []).forEach(info => {
    if (info?.certificateNumber != null && info.expirationDate) {
      map.set(info.certificateNumber, info.expirationDate);
    }
  });

  return map;
}

export function resolveAppliedFnaExpirationDate(
  reservation?: FnaReservationDetail | null,
  expirationByCertificateNumber?: Map<number, string>
): string | undefined {
  if (reservation?.expirationDate) {
    return reservation.expirationDate;
  }

  const certificateNumber = reservation?.certificateNumber;
  if (certificateNumber != null && expirationByCertificateNumber) {
    return expirationByCertificateNumber.get(certificateNumber);
  }

  return undefined;
}

type TripEdge = {
  node?: {
    items?: Array<{
      id?: string;
      basicInformation?: {
        startDate?: string | null;
        endDate?: string | null;
        confirmationNumber?: string | null;
      } | null;
      property?: { id?: string | null } | null;
      nuaInformation?: {
        confirmationNumber?: string | null;
        nuaRequestStatus?: {
          status?: string | null;
          nuaTokensAttached?: number | null;
        } | null;
      } | null;
    }> | null;
  } | null;
};

function isFutureDate(date?: string | null): boolean {
  if (!date) return false;
  const t = new Date(date).getTime();
  if (Number.isNaN(t)) return false;
  return t >= new Date().setHours(0, 0, 0, 0);
}

function isAwardExpired(date?: string | null): boolean {
  if (!date) return false;
  const t = new Date(date).getTime();
  if (Number.isNaN(t)) return false;
  return t < new Date().setHours(0, 0, 0, 0);
}

type NuaAvailableDetail = { count?: number; expirationDate?: string; issueDate?: string };

type NuaReservationDetail = {
  propertyId?: string | null;
  arrivalDate?: string | null;
  confirmationNumber?: string | null;
  departureDate?: string | null;
  sourceDescription?: string | null;
  statusType?: string | null;
  tokenCount?: number | null;
};

export type NuaSplitReservationRow = {
  confirmationNumber: string;
  tokenCount: number;
  expirationDate: string;
  expiryGroups: WalletNuaExpiryGroup[];
};

export const buildNuaReservationByConfirmationNumber = (
  reservationDetails?: NuaReservationDetail[] | null
): Map<string, NuaReservationDetail> => {
  const map = new Map<string, NuaReservationDetail>();

  (reservationDetails ?? []).forEach(detail => {
    const confirmationNumber = detail?.confirmationNumber?.trim();
    if (confirmationNumber) {
      map.set(confirmationNumber, detail);
    }
  });

  return map;
};

export const resolveNuaReservationTokenCount = (
  reservation?: NuaReservationDetail | null,
  attachedTokens?: number | null
): number => {
  if (reservation?.tokenCount != null && reservation.tokenCount > 0) {
    return reservation.tokenCount;
  }

  return attachedTokens && attachedTokens > 0 ? attachedTokens : 1;
};

export function getActiveNuaAvailableSummary(
  available?: {
    count?: number;
    details?: NuaAvailableDetail[];
  } | null
): { count: number; expirationDate?: string; expiryGroups: WalletNuaExpiryGroup[] } {
  const details = available?.details ?? [];
  const activeDetails = details.filter(detail => !isAwardExpired(detail.expirationDate));

  if (activeDetails.length > 0) {
    const count = activeDetails.reduce((sum, detail) => sum + (detail.count ?? 0), 0);
    const mergedByDate = new Map<string, number>();

    activeDetails
      .filter((d): d is { count: number; expirationDate: string } => !!d.expirationDate && (d.count ?? 0) > 0)
      .forEach(detail => {
        mergedByDate.set(detail.expirationDate, (mergedByDate.get(detail.expirationDate) ?? 0) + detail.count);
      });

    const sortedGroups: WalletNuaExpiryGroup[] = Array.from(mergedByDate.entries())
      .map(([expirationDate, groupCount]) => ({ expirationDate, count: groupCount }))
      .sort((a, b) => new Date(a.expirationDate).getTime() - new Date(b.expirationDate).getTime());

    const expirationDate = sortedGroups[0]?.expirationDate;
    return { count, expirationDate, expiryGroups: sortedGroups };
  }

  const aggregateCount = available?.count ?? 0;
  if (details.length === 0 && aggregateCount > 0) {
    return { count: aggregateCount, expiryGroups: [] };
  }

  return { count: 0, expiryGroups: [] };
}

export function resolveNuaAvailableSource(
  statusInfo: NuaStatusInfoEntry[],
  rewardsAvailable?: { count?: number; details?: NuaAvailableDetail[] } | null
): { count?: number; details?: NuaAvailableDetail[] } | undefined {
  const availableStatusEntry = statusInfo.find(
    entry => normalizeNuaUxlStatusType(entry?.statusType) === NUA_UXL_STATUS_TYPE.AVAILABLE
  );

  if (availableStatusEntry) {
    return { count: availableStatusEntry.count, details: availableStatusEntry.expirationDetails };
  }

  return rewardsAvailable ?? undefined;
}

/** Single available tile for all NUAs (WEB-210837). Modal uses expiryGroups for multi-date dropdown. */
export function buildAvailableNuaItemsFromGroups(
  expiryGroups: WalletNuaExpiryGroup[],
  aggregateCount = 0,
  soonestExpiration?: string
): WalletNuaItem[] {
  const totalCount =
    expiryGroups.length > 0 ? expiryGroups.reduce((sum, group) => sum + group.count, 0) : aggregateCount;

  if (totalCount <= 0) {
    return [];
  }

  return [
    {
      id: WALLET_NUA_AVAILABLE_ITEM_ID,
      count: totalCount,
      expirationDate: expiryGroups[0]?.expirationDate ?? soonestExpiration,
      expiryGroups: expiryGroups.length > 0 ? expiryGroups : undefined,
      status: WalletNightlyUpgradeAwardsSectionStatus.Available,
    },
  ];
}

/** Collapse per-date Available rows into one clubbed tile. Pending/approved stay separate. */
export function mergeAvailableNuaItemsIntoSingleTile(availableItems: WalletNuaItem[]): WalletNuaItem[] {
  if (availableItems.length <= 1) {
    return availableItems;
  }

  const mergedByDate = new Map<string, number>();
  let aggregateCount = 0;

  availableItems.forEach(item => {
    aggregateCount += item.count || 0;

    if (item.expiryGroups?.length) {
      item.expiryGroups.forEach(group => {
        mergedByDate.set(group.expirationDate, (mergedByDate.get(group.expirationDate) ?? 0) + group.count);
      });
      return;
    }

    if (item.expirationDate && (item.count ?? 0) > 0) {
      mergedByDate.set(item.expirationDate, (mergedByDate.get(item.expirationDate) ?? 0) + (item.count ?? 0));
    }
  });

  const expiryGroups = Array.from(mergedByDate.entries())
    .map(([expirationDate, count]) => ({ expirationDate, count }))
    .sort((a, b) => new Date(a.expirationDate).getTime() - new Date(b.expirationDate).getTime());

  return buildAvailableNuaItemsFromGroups(
    expiryGroups,
    aggregateCount,
    availableItems[0]?.expirationDate ?? expiryGroups[0]?.expirationDate
  );
}

export function getNuaStatusExpiryGroups(
  statusInfo: NuaStatusInfoEntry[],
  acceptedStatusTypes: readonly string[],
  options?: { includeExpired?: boolean }
): WalletNuaExpiryGroup[] {
  const accepted = new Set(acceptedStatusTypes.map(normalizeNuaUxlStatusType));
  const mergedByDate = new Map<string, number>();
  const includeExpired = options?.includeExpired ?? false;

  statusInfo.forEach(entry => {
    if (!accepted.has(normalizeNuaUxlStatusType(entry.statusType))) {
      return;
    }

    (entry.expirationDetails ?? []).forEach(detail => {
      if (!detail.expirationDate || (!includeExpired && isAwardExpired(detail.expirationDate))) {
        return;
      }

      const detailCount = detail.count ?? 0;
      if (detailCount <= 0) {
        return;
      }

      mergedByDate.set(detail.expirationDate, (mergedByDate.get(detail.expirationDate) ?? 0) + detailCount);
    });
  });

  return Array.from(mergedByDate.entries())
    .map(([expirationDate, count]) => ({ expirationDate, count }))
    .sort((a, b) => new Date(a.expirationDate).getTime() - new Date(b.expirationDate).getTime());
}

const NUA_RESERVATION_EXCLUDED_STATUS_TYPES = new Set(['DELETED']);

export function isPastNuaReservationStay(detail: NuaReservationDetail): boolean {
  if (!detail.departureDate) {
    return false;
  }

  return isAwardExpired(detail.departureDate);
}

/** Filters reservationDetails for FIFO split (iOS order preserved). */
export function filterNuaReservationDetailsForSplit(
  reservationDetails: NuaReservationDetail[],
  reservationStatusTypes: readonly string[]
): NuaReservationDetail[] {
  const accepted = new Set(reservationStatusTypes.map(normalizeNuaUxlStatusType));

  return reservationDetails.filter(detail => {
    if (!detail.confirmationNumber?.trim()) {
      return false;
    }

    if (isPastNuaReservationStay(detail)) {
      return false;
    }

    const statusType = normalizeNuaUxlStatusType(detail.statusType ?? undefined);
    if (NUA_RESERVATION_EXCLUDED_STATUS_TYPES.has(statusType)) {
      return false;
    }

    return accepted.has(statusType);
  });
}

const consumeEarliestNuaBuckets = (
  remaining: WalletNuaExpiryGroup[],
  tokens: number
): WalletNuaExpiryGroup[] | undefined => {
  let tokensLeft = tokens;
  const consumed: WalletNuaExpiryGroup[] = [];
  const plannedTakes: Array<{ index: number; take: number }> = [];

  remaining.forEach((bucket, index) => {
    if (tokensLeft <= 0 || bucket.count <= 0) {
      return;
    }

    const take = Math.min(bucket.count, tokensLeft);
    plannedTakes.push({ index, take });
    consumed.push({ count: take, expirationDate: bucket.expirationDate });
    tokensLeft -= take;
  });

  if (tokensLeft > 0 || consumed.length === 0) {
    return undefined;
  }

  plannedTakes.forEach(({ index, take }) => {
    remaining[index].count -= take;
  });

  return consumed;
};

/**
 * Map expiry buckets onto reservations in UXL order.
 * 1. Exact count match against the earliest remaining bucket (2-night stay ↔ 2 NUAs on one date).
 * 2. Fallback: FIFO from earliest remaining buckets. A stay that spans dates
 *    keeps every consumed group so the modal accordion can list them.
 *    Leftover buckets stay unassigned unless another trip exists to consume them.
 */
export function splitNuaByReservation(
  details: NuaReservationDetail[],
  buckets: WalletNuaExpiryGroup[]
): NuaSplitReservationRow[] {
  if (buckets.length === 0) {
    return [];
  }

  const remaining = buckets.map(bucket => ({ ...bucket }));
  const rows: NuaSplitReservationRow[] = [];

  details.forEach(detail => {
    const confirmationNumber = detail.confirmationNumber?.trim();
    const tokens = detail.tokenCount;

    if (!confirmationNumber || tokens == null || tokens <= 0) {
      return;
    }

    const nextIndex = remaining.findIndex(bucket => bucket.count > 0);
    const nextBucket = nextIndex >= 0 ? remaining[nextIndex] : undefined;
    let expiryGroups: WalletNuaExpiryGroup[] | undefined;

    if (nextBucket && nextBucket.count === tokens) {
      expiryGroups = [{ count: nextBucket.count, expirationDate: nextBucket.expirationDate }];
      remaining[nextIndex].count = 0;
    } else {
      expiryGroups = consumeEarliestNuaBuckets(remaining, tokens);
    }

    if (!expiryGroups?.length) {
      return;
    }

    rows.push({
      confirmationNumber,
      tokenCount: tokens,
      expirationDate: expiryGroups[0].expirationDate,
      expiryGroups,
    });
  });

  return rows;
}

export function buildNuaTripExpiryByConfirmation(
  statusInfo: NuaStatusInfoEntry[],
  reservationDetails: NuaReservationDetail[],
  statusInfoStatusTypes: readonly string[],
  reservationStatusTypes: readonly string[]
): Map<string, NuaSplitReservationRow> {
  const buckets = getNuaStatusExpiryGroups(statusInfo, statusInfoStatusTypes);
  const details = filterNuaReservationDetailsForSplit(reservationDetails, reservationStatusTypes);
  const rows = splitNuaByReservation(details, buckets);

  return new Map(rows.map(row => [row.confirmationNumber, row]));
}

/** Approved FIFO: prefer Guaranteed statusInfo; fall back to Requested when UXL omits Guaranteed bucket. */
export function buildNuaApprovedExpiryByConfirmation(
  statusInfo: NuaStatusInfoEntry[],
  reservationDetails: NuaReservationDetail[]
): Map<string, NuaSplitReservationRow> {
  const guaranteedBuckets = getNuaStatusExpiryGroups(statusInfo, NUA_UXL_STATUS_INFO_GUARANTEED);

  if (guaranteedBuckets.length > 0) {
    return buildNuaTripExpiryByConfirmation(
      statusInfo,
      reservationDetails,
      NUA_UXL_STATUS_INFO_GUARANTEED,
      NUA_UXL_RESERVATION_STATUS_FOR_GUARANTEED
    );
  }

  return buildNuaTripExpiryByConfirmation(
    statusInfo,
    reservationDetails,
    NUA_UXL_STATUS_INFO_PENDING,
    NUA_UXL_RESERVATION_STATUS_FOR_GUARANTEED
  );
}

export function resolveNuaTripExpiryFromSplit(
  confirmationNumber: string | undefined,
  expiryByConfirmation: Map<string, NuaSplitReservationRow>
): { expirationDate?: string; expiryGroups?: WalletNuaExpiryGroup[] } {
  if (!confirmationNumber) {
    return {};
  }

  const normalizedConfirmation = confirmationNumber.trim();
  const row = expiryByConfirmation.get(normalizedConfirmation);
  if (!row) {
    return {};
  }

  return {
    expirationDate: row.expirationDate,
    expiryGroups: row.expiryGroups,
  };
}

/** Award expiry groups sorted by date ascending. Never use stay check-in. */
export function resolveNuaTripAwardExpiration(statusExpiryGroups?: WalletNuaExpiryGroup[]): {
  expirationDate?: string;
  expiryGroups?: WalletNuaExpiryGroup[];
} {
  if (!statusExpiryGroups?.length) {
    return {};
  }

  return {
    expirationDate: statusExpiryGroups[0].expirationDate,
    expiryGroups: statusExpiryGroups,
  };
}

export const resolveNuaTripConfirmationNumber = (
  nuaConfirmationNumber?: string | null,
  tripConfirmationNumber?: string | null
): string | undefined => {
  const nuaConfirmation = nuaConfirmationNumber?.trim();
  const tripConfirmation = tripConfirmationNumber?.trim();
  return nuaConfirmation || tripConfirmation || undefined;
};

/** Pending/approved: one card per confirmation (hotel trip attached). Available: one clubbed tile (WEB-210837). */
export function mergeNuaTripItemsByHotelAndStatus(items: WalletNuaItem[]): WalletNuaItem[] {
  const available: WalletNuaItem[] = [];
  const grouped = new Map<string, WalletNuaItem[]>();

  items.forEach(item => {
    if (item.status === WalletNightlyUpgradeAwardsSectionStatus.Available) {
      available.push(item);
      return;
    }

    const stayKey = item.confirmationNumber?.trim() || item.propertyId || item.id;
    const key = `${item.status}:${stayKey}`;
    const group = grouped.get(key) ?? [];
    group.push(item);
    grouped.set(key, group);
  });

  const mergedTripItems = Array.from(grouped.values()).map(group => {
    if (group.length === 1) {
      return group[0];
    }

    const primary = [...group].sort((a, b) => {
      const at = a.checkInDate ? new Date(a.checkInDate).getTime() : Number.MAX_SAFE_INTEGER;
      const bt = b.checkInDate ? new Date(b.checkInDate).getTime() : Number.MAX_SAFE_INTEGER;
      return at - bt;
    })[0];

    return {
      ...primary,
      id: `nua-${primary.status}-${primary.confirmationNumber ?? primary.propertyId ?? primary.id}`,
      count: group.reduce((sum, item) => sum + (item.count || 0), 0),
    };
  });

  return [...mergedTripItems, ...mergeAvailableNuaItemsIntoSingleTile(available)];
}

function classifyNuaStatus(raw?: string | null): WalletNuaItem['status'] | null {
  if (!raw) return null;
  const norm = normalizeNuaUxlStatusType(raw);
  if ((NUA_UXL_TRIP_STATUS_APPROVED as readonly string[]).includes(norm)) {
    return WalletNightlyUpgradeAwardsSectionStatus.Approved;
  }
  if ((NUA_UXL_TRIP_STATUS_PENDING as readonly string[]).includes(norm)) {
    return WalletNightlyUpgradeAwardsSectionStatus.RequestPending;
  }
  return null;
}

const NUA_APPROVED_RESERVATION_STATUS_TYPES = new Set(
  NUA_UXL_RESERVATION_STATUS_FOR_GUARANTEED.map(normalizeNuaUxlStatusType)
);

export function isNuaReservationStatusEligibleForApproved(statusType?: string | null): boolean {
  const normalized = normalizeNuaUxlStatusType(statusType ?? undefined);
  if (!normalized) {
    return true;
  }

  return NUA_APPROVED_RESERVATION_STATUS_TYPES.has(normalized);
}

export function hasNuaStatusInfoBucket(
  statusInfo: NuaStatusInfoEntry[],
  acceptedStatusTypes: readonly string[]
): boolean {
  const accepted = new Set(acceptedStatusTypes.map(normalizeNuaUxlStatusType));
  return statusInfo.some(entry => accepted.has(normalizeNuaUxlStatusType(entry?.statusType)));
}

type PropertyBasic = {
  id?: string;
  basicInformation?: { name?: string | null; brand?: { id?: string | null } | null } | null;
  media?: {
    primaryImage?: {
      edges?: Array<{
        node?: { imageUrls?: { square?: string | null; wideHorizontal?: string | null } | null } | null;
      }> | null;
    } | null;
    photoGallery?: {
      hotelView?: {
        edges?: Array<{
          node?: { imageUrls?: { square?: string | null; wideHorizontal?: string | null } | null } | null;
        }> | null;
      } | null;
    } | null;
  } | null;
};

function pickPropertyImage(p?: PropertyBasic, _locale = 'en-US'): { src?: string; alt?: string } {
  const src = getHotelImageUrl(p, _locale);
  return { src, alt: p?.basicInformation?.name || undefined };
}

type UseWalletAwardsPageContext = {
  sessionData?: {
    cacheData?: {
      data?: {
        consumerID?: string;
        memberLevel?: string;
      };
    };
  };
  headersData?: Record<string, string | undefined>;
};

type UseWalletAwardsArgs = {
  pageContext: UseWalletAwardsPageContext;
  isLocal: boolean;
  currentLocale?: string;
  skip?: boolean;
};

type NuaStatusInfoEntry = { count?: number; statusType?: string; expirationDetails?: NuaAvailableDetail[] };

export function useWalletAwards({ pageContext, isLocal, currentLocale = 'en-US', skip = false }: UseWalletAwardsArgs) {
  const sessionData = pageContext?.sessionData?.cacheData?.data;
  const customerId = sessionData?.consumerID;

  const shouldSkip = skip || !customerId;

  const {
    data: rewardsData,
    loading: rewardsLoading,
    error: rewardsError,
  } = useQuery(phoenixAccountDttGetMyActivityRewardsEarned, {
    variables: { customerId },
    context: generateApolloClientHeaders(isLocal, pageContext),
    fetchPolicy: 'no-cache',
    skip: shouldSkip,
    errorPolicy: 'all',
  });

  const {
    data: currenciesData,
    loading: currenciesLoading,
    error: currenciesError,
  } = useQuery(phoenixAccountDttGetLoyaltyCurrencies, {
    variables: { customerId },
    context: generateApolloClientHeaders(isLocal, pageContext),
    fetchPolicy: 'no-cache',
    skip: shouldSkip,
    errorPolicy: 'all',
  });

  const {
    data: tripsData,
    loading: tripsLoading,
    error: _tripsError,
  } = useQuery(phoenixAccountDttGetUpcomingCustomerTrips, {
    variables: {
      customerId,
      status: CustomerTripOrderStatus.active,
      limit: 25,
      offset: 0,
      source: GRAPHQL_PAYLOAD_CONST.source,
    },
    context: generateApolloClientHeaders(isLocal, pageContext),
    fetchPolicy: 'no-cache',
    skip: shouldSkip,
    errorPolicy: 'all',
  });

  const [fetchBasicInfo, { data: basicInfoData, loading: basicInfoLoading, error: _basicInfoError }] = useLazyQuery(
    phoenixAccountGetUpcomingTripsBasicInfo
  );

  const upcomingOrderItems = useMemo(() => {
    const edges: TripEdge[] = (tripsData?.customer?.ordersByStatus?.edges as TripEdge[]) ?? [];
    return edges.flatMap(e => e?.node?.items ?? []).filter(Boolean);
  }, [tripsData]);

  const fnaReservationDetails = useMemo<FnaReservationDetail[]>(() => {
    return currenciesData?.loyalty?.benefits?.currenciesByCustomerId?.fna?.reservationDetails ?? [];
  }, [currenciesData]);

  const fnaExpirationByCertificateNumber = useMemo(
    () =>
      buildFnaExpirationByCertificateNumber(
        currenciesData?.loyalty?.benefits?.currenciesByCustomerId?.fna?.availableInfo
      ),
    [currenciesData]
  );

  const nuaReservationDetails = useMemo<NuaReservationDetail[]>(() => {
    return currenciesData?.loyalty?.benefits?.currenciesByCustomerId?.nua?.reservationDetails ?? [];
  }, [currenciesData]);

  const propertyIdsNeeded = useMemo(() => {
    const ids = new Set<string>();
    // NUA approved/pending tiles
    upcomingOrderItems.forEach(item => {
      const nuaStatus = classifyNuaStatus(item?.nuaInformation?.nuaRequestStatus?.status);
      if (nuaStatus && item?.property?.id) {
        ids.add(item.property.id);
      }
    });
    // Applied FNA tiles
    fnaReservationDetails.forEach(r => {
      if (r?.propertyId) ids.add(r.propertyId);
    });
    return Array.from(ids);
  }, [upcomingOrderItems, fnaReservationDetails]);

  const [lastIdsKey, setLastIdsKey] = useState<string>('');
  const pageContextRef = useRef(pageContext);
  pageContextRef.current = pageContext;

  useEffect(() => {
    if (shouldSkip || propertyIdsNeeded.length === 0) return;
    const key = propertyIdsNeeded.slice().sort().join(',');
    if (key === lastIdsKey) return;
    setLastIdsKey(key);
    fetchBasicInfo({
      variables: { ids: propertyIdsNeeded },
      context: generateApolloClientHeaders(isLocal, pageContextRef.current),
    });
  }, [shouldSkip, propertyIdsNeeded, lastIdsKey, fetchBasicInfo, isLocal]);

  const propertiesById = useMemo<Record<string, PropertyBasic>>(() => {
    const raw: PropertyBasic[] = basicInfoData?.propertiesByIds ?? [];
    const list: PropertyBasic[] = updateImageDomain(raw, currentLocale);
    return list.reduce<Record<string, PropertyBasic>>((acc, p) => {
      if (p?.id) acc[p.id] = p;
      return acc;
    }, {});
  }, [basicInfoData, currentLocale]);

  const fnaItems = useMemo<WalletFnaItem[]>(() => {
    const certEdges: CertEdge[] = rewardsData?.customer?.loyaltyInformation?.certificates?.edges ?? [];

    // Applied tiles -- one per reservation entry from currenciesByCustomerId
    const appliedItems: WalletFnaItem[] = fnaReservationDetails.map((r, idx) => {
      const propertyId = r?.propertyId || undefined;
      const propBasic = propertyId ? propertiesById[propertyId] : undefined;
      const propImage = pickPropertyImage(propBasic, currentLocale);

      const checkInDate = r?.arrivalDate || undefined;
      let checkOutDate: string | undefined;
      if (checkInDate) {
        const nights = 1;
        const d = new Date(checkInDate);
        d.setDate(d.getDate() + nights);
        checkOutDate = d.toISOString().split('T')[0];
      }

      return {
        id: `fna-applied-${r?.certificateNumber ?? idx}`,
        awardDescription: r?.certificateSource || undefined,
        expirationDate: resolveAppliedFnaExpirationDate(r, fnaExpirationByCertificateNumber),
        status: 'applied',
        propertyId,
        brandId: propBasic?.basicInformation?.brand?.id || undefined,
        propertyName: propBasic?.basicInformation?.name || undefined,
        propertyImageSrc: propImage.src,
        propertyImageAltText: propImage.alt,
        checkInDate,
        checkOutDate,
        confirmationNumber: r?.confirmationNumber || undefined,
      };
    });

    const availableItems: WalletFnaItem[] = certEdges.map((edge, idx) => {
      const node = edge?.node;
      return {
        id: `fna-available-${idx}-${node?.awardType?.code ?? 'cert'}`,
        awardCode: node?.awardType?.code || undefined,
        awardDescription: node?.awardType?.description || node?.awardType?.label || undefined,
        expirationDate: node?.expirationDate || undefined,
        status: 'available',
        numberOfNights: node?.numberOfNights ?? undefined,
        isCancellable: node?.isCancellable ?? undefined,
      };
    });

    const sortedAvailable = availableItems
      .filter(item => !isAwardExpired(item.expirationDate))
      .sort((a, b) => {
        const at = a.expirationDate ? new Date(a.expirationDate).getTime() : Number.MAX_SAFE_INTEGER;
        const bt = b.expirationDate ? new Date(b.expirationDate).getTime() : Number.MAX_SAFE_INTEGER;
        return at - bt;
      });

    return [...appliedItems, ...sortedAvailable];
  }, [rewardsData, fnaReservationDetails, fnaExpirationByCertificateNumber, propertiesById, currentLocale]);

  const nuaItems = useMemo<WalletNuaItem[]>(() => {
    const nuaStatusInfo: NuaStatusInfoEntry[] =
      (currenciesData?.['loyalty']?.['benefits']?.['currenciesByCustomerId']?.['nua']?.['statusInfo'] as
        | NuaStatusInfoEntry[]
        | undefined) ?? [];

    const {
      count: availableCount,
      expirationDate: soonestExpiration,
      expiryGroups,
    } = getActiveNuaAvailableSummary(
      resolveNuaAvailableSource(
        nuaStatusInfo,
        rewardsData?.customer?.loyaltyInformation?.nightlyUpgradeAwards?.available
      )
    );

    const nuaReservationsByConfirmation = buildNuaReservationByConfirmationNumber(nuaReservationDetails);

    const pendingExpiryByConfirmation = buildNuaTripExpiryByConfirmation(
      nuaStatusInfo,
      nuaReservationDetails,
      NUA_UXL_STATUS_INFO_PENDING,
      NUA_UXL_RESERVATION_STATUS_FOR_REQUESTED
    );

    const approvedExpiryByConfirmation = buildNuaApprovedExpiryByConfirmation(nuaStatusInfo, nuaReservationDetails);

    const items: WalletNuaItem[] = [];

    upcomingOrderItems.forEach((trip, idx) => {
      const statusKind = classifyNuaStatus(trip?.nuaInformation?.nuaRequestStatus?.status);
      if (!statusKind) return;
      const propertyId = trip?.property?.id || undefined;
      const propBasic = propertyId ? propertiesById[propertyId] : undefined;
      const propImage = pickPropertyImage(propBasic, currentLocale);
      const confirmationNumber = resolveNuaTripConfirmationNumber(
        trip?.nuaInformation?.confirmationNumber,
        trip?.basicInformation?.confirmationNumber
      );
      const reservation = confirmationNumber ? nuaReservationsByConfirmation.get(confirmationNumber) : undefined;
      if (statusKind === WalletNightlyUpgradeAwardsSectionStatus.Approved) {
        if (!hasNuaStatusInfoBucket(nuaStatusInfo, NUA_UXL_STATUS_INFO_GUARANTEED)) {
          return;
        }
        if (reservation && !isNuaReservationStatusEligibleForApproved(reservation.statusType)) {
          return;
        }
      }
      const attachedTokens = trip?.nuaInformation?.nuaRequestStatus?.nuaTokensAttached ?? 1;
      const tokens = resolveNuaReservationTokenCount(reservation, attachedTokens);
      const expiryByConfirmation =
        statusKind === WalletNightlyUpgradeAwardsSectionStatus.RequestPending
          ? pendingExpiryByConfirmation
          : approvedExpiryByConfirmation;
      const { expirationDate, expiryGroups: tripExpiryGroups } = resolveNuaTripExpiryFromSplit(
        confirmationNumber,
        expiryByConfirmation
      );
      items.push({
        id: `nua-${statusKind}-${confirmationNumber ?? trip?.id ?? propertyId ?? idx}`,
        count: tokens,
        expirationDate,
        expiryGroups: tripExpiryGroups,
        status: statusKind,
        propertyId: reservation?.propertyId || propertyId,
        brandId: propBasic?.basicInformation?.brand?.id || undefined,
        propertyName: propBasic?.basicInformation?.name || undefined,
        propertyImageSrc: propImage.src,
        propertyImageAltText: propImage.alt,
        checkInDate: trip?.basicInformation?.startDate || reservation?.arrivalDate || undefined,
        checkOutDate: trip?.basicInformation?.endDate || reservation?.departureDate || undefined,
        confirmationNumber,
      });
    });

    items.push(...buildAvailableNuaItemsFromGroups(expiryGroups, availableCount, soonestExpiration));

    return mergeNuaTripItemsByHotelAndStatus(items)
      .map(item => {
        if (item.status === WalletNightlyUpgradeAwardsSectionStatus.Available) {
          return item;
        }

        const reservation = item.confirmationNumber
          ? nuaReservationsByConfirmation.get(item.confirmationNumber)
          : undefined;
        if (!reservation) {
          return item;
        }

        return {
          ...item,
          count: resolveNuaReservationTokenCount(reservation, item.count),
          propertyId: reservation.propertyId || item.propertyId,
          checkInDate: item.checkInDate || reservation.arrivalDate || undefined,
          checkOutDate: item.checkOutDate || reservation.departureDate || undefined,
        };
      })
      .sort((a, b) => {
        const at = a.expirationDate ? new Date(a.expirationDate).getTime() : Number.MAX_SAFE_INTEGER;
        const bt = b.expirationDate ? new Date(b.expirationDate).getTime() : Number.MAX_SAFE_INTEGER;

        if (at !== bt) {
          return at - bt;
        }

        const statusOrder = { available: 0, approved: 1, requestPending: 2 };
        return (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3);
      });
  }, [currenciesData, rewardsData, upcomingOrderItems, nuaReservationDetails, propertiesById, currentLocale]);

  return {
    fnaItems,
    nuaItems,
    isLoading: rewardsLoading || currenciesLoading || tripsLoading || basicInfoLoading,
    // Section-level UXL error only when core award inventory cannot load.
    // Trips and basicInfo are enrichment calls; partial data is still useful.
    hasUxlError: Boolean(rewardsError || currenciesError),
    rewardsData,
    tripsData,
  };
}

export { isAwardExpired, isFutureDate };
